import React from "react";
import { Link } from "react-router-dom";
import { languageString } from "../../../utilities/text";
import { getUrl } from "../../../utilities/url";
import { CONTACT_EMAIL, CONTACT_NUMBER } from "../../../utilities/vars";
import PageHeader from "../../assemblies/pageHeader/PageHeader";
import MailTo from "../../blocks/mailTo/MailTo";
import "./termsConditions.css";

// IMPORTANT!
// if these terms are updated and users need to re-agree, update the TERMS_CONDITIONS_VERSION variable in src/utilities/vars

const privacyPolicyLink = `${window.location.protocol}//${window.location.host}${getUrl.privacy()}`;

export const termsConditionsContent = (
    <section className="u-textContent termsConditions">
        <p>
            Please read these Terms and Conditions (the Terms) carefully as they inform you of the terms of use of the
            Platform and the Service provided to you by us via the Platform. In particular, please carefully review the
            provisions relating to our charges (Clause 8) and the extent of our liability to you (Clause 11).
        </p>

        <p>
            The Redbox Platform is designed to promote user acquisition and is built to enhance revenue delivery. As
            such, the Platform/Service is designed for{" "}
            <strong>business purposes rather than for non-commercial use</strong>.
        </p>

        <p>
            You will be provided with these Terms and Conditions before signing up for an account with us. You should
            also print and retain a copy of them for future reference. These Terms are available in the English language
            only.
        </p>

        <div className="termsConditions-numbered">
            <h2>Information about Redbox</h2>

            <p>
                The website at domain https://astra.redboxmobile.com/ (<b>Platform</b>) is a website operated by
                Blackbox Platform Ltd registered in England and Wales under company number 09542003 with its registered
                office at Orwell House, 50 High Street, Hungerford, Berkshire, RG17 0NF. Our VAT number is GB 0954 2003
                (<b>we</b>, <b>us</b> or <b>our</b>).
            </p>

            <p>
                If users of the Platform (<b>you</b>, <b>your</b> or <b>yourself</b>) have any questions about these
                Terms or any problems accessing or using the Platform or any of its contents please contact us by
                writing to Blackbox Platform Ltd, Suite A, 24 High Street, Hungerford, Berkshire, RG17 0NF, emailing us
                at <MailTo email={CONTACT_EMAIL} /> or calling us on{" "}
                <a href={`tel:${CONTACT_NUMBER.HREF}`}>{CONTACT_NUMBER.VIEW}</a>.
            </p>

            <h2>Definitions</h2>

            <p>In these Terms:</p>

            <dl>
                <dt>Account</dt>
                <dd>
                    means an account that you create with us to access the functionality of the Platform and/or
                    Services;
                </dd>

                <dt>Agreement</dt>
                <dd>
                    means the agreement between you and us for the provision of the Platform and/or the Services,
                    incorporating these Terms and our Privacy Policy and any amendments to these documents from time to
                    time;
                </dd>

                <dt>Business Day</dt>
                <dd>means all days other than Saturdays, Sundays and bank or public holidays in England;</dd>

                <dt>Business Hours</dt>
                <dd>means between 09.00 and 17.30 GMT, Monday to Friday;</dd>

                <dt>Charges</dt>
                <dd>means the amounts payable by you to us as calculated in accordance with Clause 8 below;</dd>

                <dt>Force Majeure Event</dt>
                <dd>
                    means an event, or a series of related events, that is outside the reasonable control of the party
                    affected (including failures of or problems with the internet or a part of the internet, hacker
                    attacks, virus or other malicious software attacks or infections, power failures, industrial
                    disputes affecting any third party, changes to the law, disasters, explosions, fires, floods, riots,
                    terrorist attacks and wars);
                </dd>

                <dt>Intellectual Property Rights</dt>
                <dd>
                    means all intellectual property rights wherever in the world, whether registered or unregistered,
                    including any application or right of application for such rights (and a reference to Intellectual
                    Property Rights shall include copyright and related rights, database rights, rights in software,
                    confidential information, trade secrets, know-how, business names, trade names, trademarks, service
                    marks, passing-off rights, unfair competition rights, patents, petty patents, utility models,
                    semi-conductor topography rights and rights in designs, which is not in the Public Domain);
                </dd>

                <dt>Platform</dt>
                <dd>
                    means the software platform known as Astra that we own and operate, and that may be made available
                    to you as a service via the internet under these Terms;
                </dd>

                <dt>Privacy Policy</dt>
                <dd>
                    means our privacy policy a copy of which can be found at{" "}
                    <Link to={getUrl.privacy()}>{privacyPolicyLink}</Link>;
                </dd>

                <dt>Service(s)</dt>
                <dd>
                    means all the services provided or to be provided by us to you under these Terms, including (i)
                    access to the Platform (ii) the functionality on the Platform enabling you to manage search ad
                    campaigns and (iii) the Support Services;
                </dd>

                <dt>Support Services</dt>
                <dd>means the services described in Clause 5; and</dd>

                <dt>Term</dt>
                <dd>means the term of the Agreement.</dd>
            </dl>

            <p>In this Agreement, unless otherwise specified, a reference to:</p>

            <ul>
                <li>
                    the <b>singular</b> includes the plural and vice versa and any gender includes the others;
                </li>
                <li>
                    a <b>person</b> includes natural persons, firms, companies, corporations, states and associations
                    wherever and however incorporated or established;
                </li>
                <li>
                    <b>legislation</b> is to that legislation as amended, extended or re-enacted from time to time and
                    includes all subordinate legislation made under it;
                </li>
                <li>
                    <b>including</b>, <b>include</b>, <b>in particular</b> or any similar terms shall be construed as
                    illustrative only and shall not limit the sense of the words preceding those terms; and
                </li>
                <li>
                    <b>writing</b> or <b>written</b> includes email but not faxes.
                </li>
            </ul>

            <h2>The Terms of Service</h2>

            <p>
                You agree that you will comply with these Terms at all times while using the Platform/Service. You agree
                that failure to comply with these Terms will constitute a breach of these Terms, which may result in us
                terminating or suspending your access to the Platform and/or Service.
            </p>

            <p>
                Redbox Mobile reserves the right to vary these terms at any stage and will advise clients with 7 working
                days' notice.
            </p>

            <p>
                We may revise the provisions of these Terms from time to time, for example to reflect changes in the law
                changes to the functionality of the Platform or Service.
            </p>

            <p>
                If you do not agree to the New Terms you should immediately cease to use the Service or the Platform as
                your continued use will imply acceptance of the New Terms.
            </p>

            <h2>Your account information</h2>

            <p>
                You may arrange to view the Platform via an accompanied demo session, but if you wish to use our
                Services you will need to create an Account with us, which will require an email address and a password
                referred to as your login details. You may also sign up using the Google integrated log-in feature. We
                will hold your login details and any personal data you provide in accordance with our Privacy Policy.
            </p>

            <p>
                It is important that you treat your login details as confidential, and that you do not disclose them to
                any third party. If you suspect or know of any breach of your account details, you must notify us
                immediately at <MailTo email={CONTACT_EMAIL} />.
            </p>

            <p>
                We have the right to suspend or withdraw your Account and disable any user identification code or
                password, whether chosen by you or allocated by us, if in our opinion you have failed to comply with any
                of the provisions of these Terms.
            </p>

            <p>
                The internet is not a secure medium and privacy cannot be assured. We therefore do not accept
                responsibility for any harm or damage you may experience or incur by sending personal or confidential
                information to us over the internet or if we send you such information at your request.
            </p>

            <h2>The services</h2>

            <p>
                The Platform is equipped with a Contact Form Help facility, via which users may log support requests
                24/7.
            </p>

            <p>The Contact Form facility is for the purpose of providing the following support services:</p>

            <ul>
                <li>assisting you with the proper use of the Platform; and/or</li>
                <li>
                    using our reasonable endeavours to determine the causes of errors and fixing errors in the Platform
                </li>
            </ul>

            <p>
                On Business Days, we will make available an helpdesk facility between the hours of 09.00 to 17.30 London
                time for the purpose of providing the following support services (Support Services):
            </p>

            <ul>
                <li>assisting you with the proper use of the Platform; and/or </li>
                <li>
                    using our reasonable endeavours to determine the causes of errors and fixing errors in the Platform.
                </li>
            </ul>

            <p>On Business Days, the helpdesk facility will be manned between the hours of 09.00 to 17.30 GMT.</p>

            <p>
                You must make requests for Support Services through the Contact Form or by communicating directly with
                your Customer Success Account Manager.
            </p>

            <p>
                We will use reasonable endeavours to respond to requests for Support Services in accordance with your
                membership level and the nature of the issue:
            </p>

            <table>
                <thead>
                    <tr>
                        <th>Category of issue</th>
                        <th>Basic Membership</th>
                        <th>Advanced Membership</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>
                            P1 - The Service is down, and is not responding to requests or serving content, which is
                            causing critical impact to business operations; no workaround available.
                        </td>
                        <td>2 hours</td>
                        <td>2 hours</td>
                    </tr>
                    <tr>
                        <td>
                            P2 - service is responding and functional but performance is degraded and/or potential
                            impact to some aspects of business operations. Examples include, dashboard used to configure
                            the product is not operational.
                        </td>
                        <td>4 hours</td>
                        <td>4 hours</td>
                    </tr>
                    <tr>
                        <td>P3 - Non-critical issue, no business impact but user experience may not be optimal.</td>
                        <td>2 business days</td>
                        <td>1 business days</td>
                    </tr>
                </tbody>
            </table>

            <p>
                You acknowledge that from time to time we may introduce new versions of, and/or updates to, the Platform
                whether for the purpose of fixing an error, bug or other issue in the Platform or enhancing the
                functionality of the Platform. Such upgrades may result in changes to the appearance and/or
                functionality of the Platform.
            </p>

            <p>
                We may suspend access to the Platform at any time in order to carry out scheduled or emergency
                maintenance. We shall aim to give you at least 24 hours advanced written notice of any scheduled
                maintenance, which will usually be carried out outside the hours of 09.00 to 17.30 GMT
            </p>

            <h2>Acceptable use of the site/services</h2>

            <p>You must not use the Services:</p>

            <ul>
                <li>
                    in any way that causes, or may cause, damage to the Services or impairment of the availability or
                    accessibility of the Services;
                </li>
                <li>
                    in any way that is unlawful, illegal, fraudulent or harmful or engage in any abusive or threatening
                    behaviour in relation to any of our personnel, customers or users;
                </li>
                <li>in connection with any unlawful, illegal, fraudulent or harmful purpose or activity;</li>
                <li>
                    via any bot or automated computer system, and you must not enable any bot or automated computer
                    system to use the Services;
                </li>
                <li>to conduct systematic or automated data collection activities;</li>
                <li>
                    to store, host, copy, distribute, display, publish, transmit or send data that are illegal or
                    unlawful, or that will or may infringe a third party's legal rights, or that could give rise to
                    legal action whether against you or us or a third party (in each case in any jurisdiction and under
                    any applicable law).
                </li>
            </ul>

            <p>You must not use the Services to store, process, share or transmit:</p>

            <ul>
                <li>
                    any viruses, Trojans, worms, root kits, spyware or other harmful software, programs, routines,
                    applications or technologies; or
                </li>
                <li>
                    any software, programs, routines, applications or technologies that will or may negatively affect
                    the performance of a computer or present significant security risks to a computer.
                </li>
            </ul>

            <p>
                Without prejudice to this general right and our other legal rights, if you breach this Clause 6 in any
                way, or if we reasonably suspect that you have breached this Clause 6 in any way, we may:
            </p>

            <ul>
                <li>delete or edit any of your data;</li>
                <li>send you one or more formal warnings;</li>
                <li>temporarily suspend your access to a part or all of the Services; and/or</li>
                <li>permanently prohibit you from using a part or all of the Services.</li>
            </ul>

            <p>
                Where we suspend or prohibit your access to the Services or a part of the Services, you must not take
                any action to circumvent such suspension or prohibition (including without limitation using a different
                account).
            </p>

            <h2>Intellectual property rights</h2>

            <p>
                We are the owner or licensee of all Intellectual Property Rights in the Platform and Service and all
                design, text, pictures, graphics (and the selection and arrangement of them), software compilations,
                coding, underlying source code, software and all such materials forming part of the Platform (Our
                Content). All rights not expressly granted to you under these Terms are expressly reserved by us and our
                licensors.
            </p>

            <p>
                You acknowledge and agree that all content and information on the Platform is protected by proprietary
                rights and laws. You agree not to modify, copy, distribute, transmit, display, perform, reproduce,
                publish, licence, transfer, create derivative work from, sell or re-sell any content or information
                obtained from or through the Platform.
            </p>

            <p>
                Subject to these Terms, we hereby grant to you a non-exclusive, non-transferable, non-sublicensable,
                limited licence to use the features and functionality of the Platform for the purpose of using the
                Services during the Term.
            </p>

            <p>You shall not:</p>

            <ul>
                <li>
                    remove any product identification, trademark, copyright, patent, or other notices or markings
                    contained in, displayed by, or provided with the Platform;
                </li>
                <li>
                    access or use the Platform in order to build any software, product, or service that is competitive
                    or similar to the Software or any portion thereof;
                </li>
                <li>
                    enter into time-sharing or data processing service arrangements involving use of the Platform with
                    any third party;
                </li>
                <li>
                    create internet links to the Platform or frame or mirror any content on any other server or wireless
                    or Internet-based device;
                </li>
                <li>
                    decompile, disassemble, or reverse engineer any object code that is part of the Platform or attempt
                    to reverse engineer, reconstruct, identify, or discover any source code of any such software, the
                    structure, sequence, or organisation of such source code or any algorithms, methods, or models
                    contained therein; or
                </li>
                <li>modify or improve the Platform or make derivative works based upon the Platform.</li>
            </ul>

            <p>
                You grant to us a worldwide, irrevocable, non-exclusive, sublicensable royalty-free licence to store,
                copy and transmit your app data for the purpose of providing the Services.
            </p>

            <h2>Charges, payment and VAT</h2>

            <p>
                Each time you create a campaign or increase your Budget for an existing campaign we will charge you the
                percentage fee commensurate with your Platform membership, in consideration for your use of the
                Platform/Services (our <b>Charges</b>).
            </p>

            <p>
                Once committed, the full amount of your Budget (including our Charges) is non-refundable at any time
                during the Term of this Agreement or on termination by either party of this Agreement; except in
                accordance with the refund policy relating to unsatisfactory provision of the service or failure to meet
                outlined terms.
            </p>

            <p>
                We may change the Charges in the future with 30 days' notice. If you object to any variation of the
                Charges, you should stop using the Services.
            </p>

            <p>
                As stated in your agreement with us, we will charge you a fee to use the Platform; the fee is a
                percentage of your monthly media spend through the Platform, or $250 a month - whichever is the greater.
            </p>

            <p>Invoicing occurs at the month-end. Payment terms are 30 days from the date of invoice.</p>

            <p>
                For UK registered companies, VAT will be applied at the time of invoicing the Platform fee at the rate
                stated in your agreement with us.
            </p>

            <p>
                You will be able to obtain copies of your invoices via the Platform or via requesting them from your
                Account Manager.
            </p>

            <p>
                If you do not pay any amount properly due to us under or in connection with these Terms we will be
                entitled to charge you interest on undisputed overdue sums at the rate of four percent (4%) per annum
                above the Bank of England base rate (both before and after judgement) provided that we shall first give
                you not less than ten (10) Business Days' written notice of its intention to charge interest, such
                notice being served no earlier than the due date for payment of the relevant overdue sum. We may also
                suspend access to the Platform and the provision of the Services if any amounts due to be paid by you to
                us under these Terms are overdue.
            </p>

            <p>
                The 'Cost Per Download (CPD) Goal' specified by the user is not an actual maximum limit imposed by the
                Platform. Instead, it is used as a guiding value. The actual CPD may differ depending on the performance
                of individual campaigns and can be higher or lower than the CPD Goal.
            </p>

            <h2>Warranties</h2>

            <p>
                We guarantee a minimum available Platform scheduled uptime of 99%. This limited warranty shall not apply
                to problems that result from (i) factors outside of The Company's reasonable control; (ii) any failure
                by the user to comply with these Terms and Conditions; (iii) any failure by the user to use the Platform
                in accordance with the documentation or other instructions provided; (iv) any unauthorised use of the
                Platform; (v) the user's or any third party's hardware, software, and equipment; (vi) the user's errors
                in entering, analysing, or reporting data; (vii) the fault or negligence of the user, operator error,
                improper use or misuse of the Platform, or any other causes external to The Company or the Platform; or
                (viii) downtime as a result of scheduled maintenance performed.
            </p>

            <p>You warrant and represent to us that:</p>

            <ul>
                <li>
                    you have the legal right and authority to enter into and perform your obligations under the
                    Agreement;
                </li>
                <li>the individual opening the Account on your behalf is authorised to bind your business; and</li>
                <li>
                    all the information that you supply to us (including all Account information) is accurate, true,
                    non-misleading, up to date and complete.
                </li>
            </ul>

            <p>We warrant to you that:</p>

            <ul>
                <li>
                    we have the legal right and authority to enter into and perform our obligations under these Terms;
                    and
                </li>
                <li>we will perform our obligations under these Terms with reasonable care and skill.</li>
            </ul>

            <p>You acknowledge that:</p>

            <ul>
                <li>
                    the Platform and the Services are made available as is, and we do not make any warranties or
                    representations as to the nature or performance of the Platform and the Services;
                </li>
                <li>
                    complex software is never wholly free from defects, errors and bugs, and we give no warranty or
                    representation that the Platform will be wholly free from such defects, errors and bugs;
                </li>
                <li>
                    complex software is never wholly free from security vulnerabilities, and we give no warranty or
                    representation that the Platform will be wholly free from such vulnerabilities;
                </li>
                <li>
                    we do not warrant or represent that the Platform will be compatible with any other application,
                    program or software;
                </li>
                <li>
                    you are responsible for determining whether the Platform and Services meet your requirements, and we
                    do not warrant or represent that the Platform or Services will meet those requirements;
                </li>
                <li>
                    we shall have no liability for any damage caused by errors or omissions in any information,
                    instructions or scripts provided to us by you in connection with the Services, or any actions taken
                    by us at your direction.
                </li>
            </ul>

            <p>
                Both of the parties' warranties and representations in respect of the subject matter of these Terms are
                expressly set out in the Terms. To the maximum extent permitted by applicable law, no other warranties
                or representations concerning the subject matter of these Terms will be implied into them.
            </p>

            <h2>Indemnity</h2>

            <p>
                You will indemnify us, and keep us indemnified, against any and all liabilities, damages, losses, costs
                and expenses (including legal expenses and amounts paid in settlement of any disputes) suffered or
                incurred by us and arising as a result of any breach by you of Clause 7.
            </p>

            <h2>Limitations and exclusions of liability</h2>

            <p>Nothing in these Terms will:</p>

            <ul>
                <li>
                    limit or exclude the liability of a party for death or personal injury resulting from negligence;
                </li>
                <li>
                    limit or exclude the liability of a party for fraud or fraudulent misrepresentation by that party;
                </li>
                <li>limit any liability of a party in any way that is not permitted under applicable law; or</li>
                <li>exclude any liability of a party that may not be excluded under applicable law.</li>
            </ul>

            <p>The limitations and exclusions of liability set out in this Clause 11 and elsewhere in these Terms:</p>

            <ul>
                <li>are subject to Clause 11.1; and</li>
                <li>
                    govern all liabilities arising under these Terms or in relation to the subject matter of the Terms,
                    including liabilities arising in contract, in tort (including negligence) and for breach of
                    statutory duty.
                </li>
            </ul>

            <p>
                We shall not be liable for any claim to the extent that it relates to loss of profits, revenue,
                goodwill, business opportunity, contracts or anticipated savings, injury to reputation, wasted
                management time, loss or corruption of any data or indirect, consequential or special loss or damage
                regardless of the form of action, whether in contract, tort (including, but not limited to, negligence)
                or strict liability or otherwise howsoever arising and regardless of whether we knew or had reason to
                know of the possibility of the loss or damage in question.
            </p>

            <p>
                Our total aggregate liability in respect of all causes of action arising out of or in connection with
                these Terms (whether for breach of contract, strict liability, tort (including negligence),
                misrepresentation or otherwise) shall not exceed 125% of the total Charges paid under these Terms.
            </p>

            <h2>Data Protection</h2>

            <p>
                For the purpose of this Clause 12, Customer Data shall mean the data inputted by you or employees in use
                of the Platform and/or Services or us on your behalf for the purpose of you using the Platform and/or
                Services or us facilitating your use of the Platform and/or Services.
            </p>

            <p>
                You shall own all right, title and interest in and to all of the Customer Data that is not personal data
                and you shall have sole responsibility for the legality, reliability, integrity of all such Customer
                Data.
            </p>

            <p>
                In the event of any loss or damage to your Customer Data, your sole and exclusive remedy against us
                shall be for us to use reasonable commercial endeavours to restore the Customer Data that has been lost
                or damaged from the latest back-up of such Customer Data maintained us. We shall not be responsible for
                any loss, destruction, alteration or disclosure of Customer Data caused by any third party (except those
                third parties sub-contracted by us to perform services related to Customer Data maintenance and back-up
                for which it shall remain fully liable under Clause 12.9).
            </p>

            <p>
                We shall, in providing the Services, comply with our Privacy Policy relating to the privacy and security
                of any personal data included in the Customer Data available at{" "}
                <Link to={getUrl.privacy()}>{privacyPolicyLink}</Link> or such other website address as we may notify
                you from time to time, as such document may be amended from time to time by us in our sole discretion.
            </p>

            <p>
                Both parties will comply with all applicable requirements of the General Data Protection Regulation (
                <b>GDPR</b>), together with the Data Protection Act 2018 and any other law applicable to the protection
                of personal data in effect from time to time (together, <b>Data Protection Legislation</b>). This Clause
                12 is in addition to, and does not relieve, remove or replace, a party's obligations under the Data
                Protection Legislation.
            </p>

            <p>The parties acknowledge that:</p>

            <ul>
                <li>
                    if we process any personal data on your behalf when performing our obligations under this Agreement,
                    you are the data controller and we are the data processor for the purposes of the Data Protection
                    Legislation (where <b>Data Controller</b>, <b>Data Processor </b>
                    have the meanings as defined in the Data Protection Legislation).
                </li>
                <li>
                    our <Link to={getUrl.privacy()}>Privacy Policy</Link> sets out the scope, nature and purpose of
                    processing by us, the duration of the processing and the types of personal data (as defined in the
                    Data Protection Legislation, <b>Personal Data</b>) and categories of <b>Data Subject</b> as defined
                    in the Data Protection Legislation).
                </li>
                <li>
                    the personal data may be transferred or stored outside the EEA or the country where you are located
                    in order to carry out the Services and our other obligations under this Agreement.
                </li>
            </ul>

            <p>
                Without prejudice to the generality of Clause 12.5, you will ensure that you have all necessary
                appropriate consents and notices in place to enable lawful transfer of the Personal Data to us for the
                duration and purposes of this Agreement so that we may lawfully use, process and transfer the Personal
                Data in accordance with this Agreement on your behalf.
            </p>

            <p>
                Without prejudice to the generality of Clause 12.5, we shall, in relation to any Personal Data processed
                in connection with the performance by us of our obligations under this Agreement:
            </p>

            <ul>
                <li>
                    process that Personal Data only on your written instructions unless we are required by the laws of
                    any member of the European Union or by the laws of the European Union applicable to us to process
                    Personal Data (<b>Applicable Laws</b>). Where we are relying on laws of a member of the European
                    Union or European Union law as the basis for processing Personal Data, we shall promptly notify you
                    of this before performing the processing required by the Applicable Laws unless those Applicable
                    Laws prohibit us from so notifying you;
                </li>
                <li>
                    not transfer any Personal Data outside of the European Economic Area and the United Kingdom unless
                    the following conditions are fulfilled;
                    <ol type="a">
                        <li>you or we have provided appropriate safeguards in relation to the transfer;</li>
                        <li>the data subject has enforceable rights and effective legal remedies;</li>
                        <li>
                            we comply with its obligations under the Data Protection Legislation by providing an
                            adequate level of protection to any Personal Data that is transferred; and
                        </li>
                        <li>
                            we comply with reasonable instructions notified to us in advance by you with respect to the
                            processing of the Personal Data;
                        </li>
                    </ol>
                </li>
                <li>
                    assist you at your cost, in responding to any request from a Data Subject and in ensuring compliance
                    with its obligations under the Data Protection Legislation with respect to security, breach
                    notifications, impact assessments and consultations with supervisory authorities or regulators;
                </li>
                <li>notify you without undue delay on becoming aware of a Personal Data breach;</li>
                <li>
                    at your written direction, delete or return Personal Data and copies thereof to you on termination
                    of the Agreement unless required by Applicable Law to store the Personal Data; and
                </li>
                <li>
                    maintain complete and accurate records and information to demonstrate our compliance with this
                    Clause 12.
                </li>
            </ul>

            <p>
                Each party shall ensure that it has in place appropriate technical and organisational measures, reviewed
                and approved by the other party, to protect against unauthorised or unlawful processing of Personal Data
                and against accidental loss or destruction of, or damage to, Personal Data, appropriate to the harm that
                might result from the unauthorised or unlawful processing or accidental loss, destruction or damage and
                the nature of the data to be protected, having regard to the state of technological development and the
                cost of implementing any measures (those measures may include, where appropriate, pseudonymising and
                encrypting Personal Data, ensuring confidentiality, integrity, availability and resilience of its
                systems and services, ensuring that availability of and access to Personal Data can be restored in a
                timely manner after an incident, and regularly assessing and evaluating the effectiveness of the
                technical and organisational measures adopted by it).
            </p>

            <p>
                You authorise us to appoint sub-processors from time to time provided that we shall notify you of any
                intended changes concerning the addition or replacement of other sub-processors and shall impose upon
                any sub-processor (and procure any sub-processor's compliance with) the terms of this Clause 12 as if
                the processing being carried out by the sub-processor was being carried out by us (and we shall be
                liable for the acts and omissions of such sub-processors as if they were our own acts and omissions)
            </p>

            <h2>Confidentiality</h2>

            <p>
                We will use reasonable endeavours to keep confidential and not disclose data to any person save as
                expressly permitted by this Clause 13.
            </p>

            <p>
                Your data may be disclosed by us to our officers, employees, agents, insurers and professional advisers,
                provided that the recipient is bound in writing to maintain the confidentiality of the material
                disclosed.
            </p>

            <p>The obligations set out in this Clause 13 shall not apply to:</p>

            <ul>
                <li>
                    information that is publicly known (other than through a breach of an obligation of confidence);
                </li>
                <li>information that was in our possession prior to disclosure by you;</li>
                <li>
                    information that is received by us from an independent third party who has a right to disclose the
                    relevant information; or
                </li>
                <li>
                    information that is required to be disclosed by law, or by a governmental authority, stock exchange
                    or regulatory body.
                </li>
            </ul>

            <h2>Termination</h2>

            <p>This Agreement shall continue until terminated by either party in accordance with this Clause 14.</p>

            <p>
                You may terminate this Agreement by closing your Account using the procedure specified on the Platform.
                For the avoidance of doubt, any attempt to terminate the Agreement by telephone or by sending an email
                message or by any means other than as specified above shall be ineffective.
            </p>

            <p>
                We may suspend or terminate your access to the Platform and/or Service immediately by giving you written
                notice of termination and/or closing the Account if you:
            </p>

            <ul>
                <li>
                    commit an irremediable breach of this Agreement, persistently commit remediable breaches or commit
                    any remediable breach and fail to remedy it within 30 days of receipt of notice of the breach
                    requiring remedy of the same; or
                </li>
                <li>
                    make an arrangement with or enter into a compromise with your creditors, become the subject of a
                    voluntary arrangement, receivership, administration, liquidation or winding up, are unable to pay
                    our debts or otherwise become insolvent or suffers or is the subject of any distraint, execution,
                    event of insolvency or event of bankruptcy or any other similar process or event, whether in the
                    United Kingdom or elsewhere
                </li>
            </ul>

            <p>We may also terminate the Agreement:</p>

            <ul>
                <li>
                    by giving you 30 days' written notice of termination if we make a good faith decision to stop
                    operating the Platform generally; or
                </li>
                <li>by giving you 90 days' written notice of termination.</li>
            </ul>

            <h2>Effects of termination</h2>

            <p>
                Upon termination of the Agreement, all the provisions of the Agreement will cease to have effect, save
                that the following provisions of the Agreement will survive and continue to have effect (in accordance
                with their terms or otherwise indefinitely): Clauses 1, 7, 8, 9, 11, 12, 13 and 18.
            </p>

            <p>
                Termination of the Agreement will not affect either party's accrued liabilities and rights as at the
                date of termination.
            </p>

            <p>
                At any time following the termination of the Agreement, we may delete from our computer systems all
                data. You acknowledge that, if you have not retrieved your data from the Platform within 5 days from the
                date of termination, you may lose the data.
            </p>

            <p>
                Notwithstanding Clause 15.3, you acknowledge that we may retain data in our systems after the date of
                termination, whether for technical reasons, legal reasons or otherwise; and the licence set out in
                Clause 7.5 shall continue after termination to the extent necessary for us to exercise our rights under
                this Clause 15.4.
            </p>

            <p>
                You will not be entitled to any refund of any Budget and/or Charges upon the termination of the
                Agreement, nor will you be released from any liability to pay any Budget and/or Charges that have
                accrued before the date of effective termination.
            </p>

            <h2>Notices</h2>

            <p>Any notice under the Agreement must be in writing.</p>

            <p>
                Any notice that we give to you under the Agreement will be sent by email to the email address that you
                supply to us as part of the Account information.
            </p>

            <p>
                Any notice that you give to us under the Agreement must be sent by post or email using the contact
                information set out in Clause 1.2 of these Terms.
            </p>

            <p>A notice shall be deemed to have been received:</p>

            <ul>
                <li>in the case of a delivery made in person, when delivered;</li>
                <li>in the case of first class post, two working days after posting; and</li>
                <li>
                    in the case of email, at the time of sending if between 9am and 4:30pm GMT a Business Day, at 9am on
                    that day if sent before 9am on a Business Day or otherwise at 9am on the next Business Day (provided
                    in each case that no email delivery failure notification is received).
                </li>
            </ul>

            <h2>Force Majeure Events</h2>

            <p>
                We shall not be liable or responsible for any failure to perform, or delay in performance of, any of our
                obligations under this Agreement that is caused by a Force Majeure Event.
            </p>

            <p>
                Our performance under this Agreement is deemed to be suspended for the period that the Force Majeure
                Event continues, and we shall have an extension of time for performance for the duration of that period.
                We shall use our reasonable endeavours to bring the Force Majeure Event to a close or to find a solution
                by which our obligations under this Agreement may be performed despite the Force Majeure Event.
            </p>

            <h2>General</h2>

            <p>
                This Agreement constitutes the entire agreement between the parties and supersedes and extinguishes all
                previous agreements, promises, assurances, warranties, representations and understandings between them,
                whether written or oral, relating to its subject matter. Each party acknowledges and accepts that, in
                entering into this Agreement, it has not relied upon any representation, undertaking or promise except
                as set out in this Agreement.
            </p>

            <p>
                Each party agrees that it shall have no remedies in respect of any statement, representation, assurance
                or warranty (whether made innocently or negligently) that is not set out in this Agreement.
            </p>

            <p>
                The failure or delay of either party to enforce or to exercise, at any time or for any period of time,
                any term of or any right, power or privilege arising pursuant to this Agreement does not constitute and
                shall not be construed as a waiver of such term or right, remedy, power or privilege and shall in no way
                affect either party's right later to enforce or exercise it, nor shall any single or partial exercise of
                any right, remedy, power or privilege preclude any further exercise of the same or the exercise of any
                other remedy, right, power or privilege.
            </p>

            <p>
                The invalidity or unenforceability of any provision of or any part of a provision of or any right
                arising pursuant to this Agreement shall not in any way affect the remaining provisions or rights, which
                shall be construed as if such invalid or unenforceable part did not exist.
            </p>

            <p>
                We may freely assign any or all of the contractual rights and/or obligations we have under or in
                connection with this Agreement to any third party. You may not, without our prior written consent,
                assign, transfer, charge, licence or otherwise dispose of or deal in your contractual rights or
                obligations under this Agreement.
            </p>

            <p>
                Nothing in this Agreement shall confer any rights upon any person who is not a party to it, whether
                under the Contracts (Rights of Third Parties) Act 1999, or otherwise.
            </p>

            <p>
                This Agreement, its subject matter and formation (including any associated non-contractual disputes or
                claims) are governed by English law and the parties hereby accept the exclusive jurisdiction of the
                courts of England and Wales.
            </p>
        </div>
    </section>
);

export default class TermsConditionsPage extends React.Component {
    render() {
        return (
            <>
                <PageHeader
                    title={languageString("pages.terms.title", "Terms and conditions")}
                    subTitle={languageString("ui.title.company", "Redbox Platform")}
                />
                <div className="u-pageWidth u-m0 u-pt32 u-pb64">{termsConditionsContent}</div>
            </>
        );
    }
}
